import { useEffect, useRef, useState } from "react";
import Button from "./Button";
import { useSendVerification, useConfirmVerification } from "../hooks/useApi";
import GlassModal from "./GlassModal";

const OTP_LENGTH = 5;
const COUNTDOWN_SECONDS = 120;

function OtpBoxes({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (idx: number, raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      // User cleared the field (backspace/delete or select+delete) — clear this box.
      const next = [...value];
      next[idx] = "";
      onChange(next);
      return;
    }
    if (digits.length > 1) {
      // Paste of a full/partial code: fill remaining boxes
      const next = [...value];
      for (let i = 0; i < digits.length && idx + i < OTP_LENGTH; i++) {
        next[idx + i] = digits[i];
      }
      onChange(next);
      const lastIdx = Math.min(idx + digits.length, OTP_LENGTH - 1);
      refs.current[lastIdx]?.focus();
      return;
    }
    const next = [...value];
    next[idx] = digits;
    onChange(next);
    if (idx < OTP_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...value];
      if (value[idx]) {
        // Filled box: clear it and move to previous so repeated backspace deletes continuously
        next[idx] = "";
        onChange(next);
        if (idx > 0) refs.current[idx - 1]?.focus();
      } else if (idx > 0) {
        // Empty box: clear previous and focus it
        next[idx - 1] = "";
        onChange(next);
        refs.current[idx - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2.5" dir="ltr">
      {Array.from({ length: OTP_LENGTH }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          inputMode="numeric"
          value={value[idx] || ""}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onFocus={(e) => e.target.select()}
          maxLength={OTP_LENGTH}
          className="w-12 h-14 text-center text-h2 font-number rounded-btn border border-line text-text-main bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
        />
      ))}
    </div>
  );
}

export default function VerificationModal({
  open,
  onClose,
  field,
  destination,
}: {
  open: boolean;
  onClose: () => void;
  field: "email" | "phone";
  destination: string;
}) {
  const send = useSendVerification();
  const confirm = useConfirmVerification();

  const [phase, setPhase] = useState<"idle" | "sent" | "done">("idle");
  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fieldLabel = field === "email" ? "ایمیل" : "شماره موبایل";

  useEffect(() => {
    if (open) {
      setPhase("idle");
      setCode(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(COUNTDOWN_SECONDS);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = () => {
    setSecondsLeft(COUNTDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleSend = () => {
    // Pass destination as value so a new phone typed in Profile but not yet saved
    // can be verified and auto-saved without requiring Save Changes.
    const payload: { field: string; value?: string } = { field };
    if (destination && destination.trim()) payload.value = destination.trim();
    send.mutate(payload, {
      onSuccess: () => {
        setPhase("sent");
        startCountdown();
      },
    });
  };

  const handleResend = () => {
    handleSend();
  };

  const handleConfirm = () => {
    const entered = code.join("");
    if (entered.length !== OTP_LENGTH) return;
    confirm.mutate(
      { field, code: entered },
      {
        onSuccess: () => {
          if (timerRef.current) clearInterval(timerRef.current);
          setPhase("done");
          setTimeout(onClose, 800);
        },
      }
    );
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <GlassModal
      open={open}
      onClose={onClose}
      title={phase === "done" ? "احراز هویت موفق" : `تأیید ${fieldLabel}`}
    >
      {phase === "idle" && (
        <div className="space-y-4">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-14 h-14 rounded-full bg-primary-light flex items-center justify-center mb-3">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                {field === "email" ? (
                  <>
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 5L2 7" />
                  </>
                ) : (
                  <>
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </>
                )}
              </svg>
            </div>
            <p className="text-body text-text-main font-medium">
              {field === "email" ? "ایمیل خود را اکنون تأیید کنید" : "شماره موبایل خود را اکنون تأیید کنید"}
            </p>
            <p className="text-small text-text-tertiary mt-1" dir="ltr">{destination}</p>
            <p className="text-small text-text-tertiary mt-3">
              یک کد ۵ رقمی برای شما ارسال خواهد شد
            </p>
          </div>
          <Button className="w-full !py-3" onClick={handleSend} loading={send.isPending}>
            ارسال کد تأیید
          </Button>
        </div>
      )}

      {phase === "sent" && (
        <div className="space-y-5">
          <div className="text-center">
            <p className="text-body text-text-main font-medium">کد تأیید را وارد کنید</p>
            <p className="text-small text-text-tertiary mt-1" dir="ltr">{destination}</p>
          </div>

          <OtpBoxes value={code} onChange={setCode} />

          <div className="flex items-center justify-center gap-2 text-small">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-text-tertiary">زمان باقی‌مانده</span>
            <span className="font-number font-semibold text-text-main" dir="ltr">
              {mm}:{ss}
            </span>
          </div>

          {secondsLeft === 0 && (
            <p className="text-center text-small" style={{ color: "var(--status-error-text)" }}>
              کد منقضی شد. دوباره ارسال کنید.
            </p>
          )}

          <div className="flex gap-2">
            <Button className="flex-1 !py-3" onClick={handleConfirm} loading={confirm.isPending} disabled={code.join("").length !== OTP_LENGTH}>
              تأیید کد
            </Button>
            <Button variant="secondary" onClick={handleResend} loading={send.isPending} disabled={secondsLeft > 0}>
              ارسال مجدد
            </Button>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <p className="text-body text-text-main font-medium">تأیید شد</p>
          <p className="text-small text-text-tertiary mt-1">{fieldLabel} شما با موفقیت تأیید شد</p>
        </div>
      )}
    </GlassModal>
  );
}
